/** This file must only contain pure code and pure imports */

import { type ICreateSceneUboOptions, Scene } from "../../scene.pure";
import { InternalTexture, InternalTextureSource } from "../../Materials/Textures/internalTexture";
import { TmpVectors, Matrix } from "../../Maths/math.vector.pure";
import { MultiviewRenderTarget } from "../../Materials/Textures/MultiviewRenderTarget";
import { Frustum } from "../../Maths/math.frustum";
import { type WebGLRenderTargetWrapper } from "../WebGL/webGLRenderTargetWrapper";
import { type RenderTargetWrapper } from "../renderTargetWrapper";
import { type AbstractEngine } from "../abstractEngine.pure";
import { Camera } from "../../Cameras/camera.pure";
import { Engine } from "../../Engines/engine.pure";
import { UniformBuffer } from "../../Materials/uniformBuffer";

let _Registered = false;
/**
 * Register side effects for engineMultiview.
 * Safe to call multiple times; only the first call has an effect.
 */
export function RegisterEngineMultiview(): void {
    if (_Registered) {
        return;
    }
    _Registered = true;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    function CreateMultiviewUbo(engine: AbstractEngine, name?: string, trackUBOsInFrame?: boolean) {
        const ubo = new UniformBuffer(engine, undefined, true, name, undefined, trackUBOsInFrame);
        ubo.addUniform("viewProjection", 16);
        ubo.addUniform("viewProjectionR", 16);
        ubo.addUniform("view", 16);
        ubo.addUniform("projection", 16);
        ubo.addUniform("vEyePosition", 4);
        ubo.addUniform("inverseProjection", 16);
        return ubo;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const CurrentCreateSceneUniformBuffer = Scene.prototype.createSceneUniformBuffer;

    Engine.prototype.createMultiviewRenderTargetTexture = function (width: number, height: number, colorTexture?: WebGLTexture, depthStencilTexture?: WebGLTexture) {
        const gl = this._gl;

        if (!this.getCaps().multiview) {
            // eslint-disable-next-line no-throw-literal
            throw "Multiview is not supported";
        }

        const rtWrapper = this._createHardwareRenderTargetWrapper(false, false, { width, height }) as WebGLRenderTargetWrapper;

        rtWrapper._framebuffer = gl.createFramebuffer();

        const internalTexture = new InternalTexture(this, InternalTextureSource.Unknown, true);
        internalTexture.width = width;
        internalTexture.height = height;
        internalTexture.isMultiview = true;

        if (!colorTexture) {
            colorTexture = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D_ARRAY, colorTexture);
            (gl as any).texStorage3D(gl.TEXTURE_2D_ARRAY, 1, gl.RGBA8, width, height, 2);
        }

        rtWrapper._colorTextureArray = colorTexture;

        if (!depthStencilTexture) {
            depthStencilTexture = gl.createTexture();
            gl.bindTexture(gl.TEXTURE_2D_ARRAY, depthStencilTexture);
            (gl as any).texStorage3D(gl.TEXTURE_2D_ARRAY, 1, (gl as any).DEPTH24_STENCIL8, width, height, 2);
        }

        rtWrapper._depthStencilTextureArray = depthStencilTexture;

        internalTexture.isReady = true;

        rtWrapper.setTextures(internalTexture);
        rtWrapper._depthStencilTexture = internalTexture;

        return rtWrapper;
    };

    Engine.prototype.bindMultiviewFramebuffer = function (_multiviewTexture: RenderTargetWrapper) {
        const multiviewTexture = _multiviewTexture as WebGLRenderTargetWrapper;

        const gl: any = this._gl;
        const ext = this.getCaps().oculusMultiview || this.getCaps().multiview;

        this.bindFramebuffer(multiviewTexture, undefined, undefined, undefined, true);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, multiviewTexture._framebuffer);
        if (multiviewTexture._colorTextureArray && multiviewTexture._depthStencilTextureArray) {
            if (this.getCaps().oculusMultiview) {
                ext.framebufferTextureMultisampleMultiviewOVR(gl.DRAW_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, multiviewTexture._colorTextureArray, 0, multiviewTexture.samples, 0, 2);
                ext.framebufferTextureMultisampleMultiviewOVR(
                    gl.DRAW_FRAMEBUFFER,
                    gl.DEPTH_STENCIL_ATTACHMENT,
                    multiviewTexture._depthStencilTextureArray,
                    0,
                    multiviewTexture.samples,
                    0,
                    2
                );
            } else {
                ext.framebufferTextureMultiviewOVR(gl.DRAW_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, multiviewTexture._colorTextureArray, 0, 0, 2);
                ext.framebufferTextureMultiviewOVR(gl.DRAW_FRAMEBUFFER, gl.DEPTH_STENCIL_ATTACHMENT, multiviewTexture._depthStencilTextureArray, 0, 0, 2);
            }
        } else {
            // eslint-disable-next-line no-throw-literal
            throw "Invalid multiview frame buffer";
        }
    };

    Engine.prototype.bindSpaceWarpFramebuffer = function (_spaceWarpTexture: RenderTargetWrapper) {
        const spaceWarpTexture = _spaceWarpTexture as WebGLRenderTargetWrapper;

        const gl: any = this._gl;
        const ext = this.getCaps().oculusMultiview || this.getCaps().multiview;

        this.bindFramebuffer(spaceWarpTexture, undefined, undefined, undefined, true);
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, spaceWarpTexture._framebuffer);
        if (spaceWarpTexture._colorTextureArray && spaceWarpTexture._depthStencilTextureArray) {
            ext.framebufferTextureMultiviewOVR(gl.DRAW_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, spaceWarpTexture._colorTextureArray, 0, 0, 2);
            ext.framebufferTextureMultiviewOVR(gl.DRAW_FRAMEBUFFER, gl.DEPTH_ATTACHMENT, spaceWarpTexture._depthStencilTextureArray, 0, 0, 2);
        } else {
            throw new Error("Invalid Space Warp framebuffer");
        }
    };

    Camera.prototype._useMultiviewToSingleView = false;

    Camera.prototype._multiviewTexture = null;

    Camera.prototype._resizeOrCreateMultiviewTexture = function (width: number, height: number) {
        if (!this._multiviewTexture) {
            this._multiviewTexture = new MultiviewRenderTarget(this.getScene(), { width: width, height: height });
        } else if (this._multiviewTexture.getRenderWidth() != width || this._multiviewTexture.getRenderHeight() != height) {
            this._multiviewTexture.dispose();
            this._multiviewTexture = new MultiviewRenderTarget(this.getScene(), { width: width, height: height });
        }
    };

    Scene.prototype._transformMatrixR = Matrix.Zero();

    Scene.prototype._multiviewSceneUbo = null;

    Scene.prototype._multiviewSceneUboIsActive = false;

    Scene.prototype._createMultiviewUbo = function () {
        this._multiviewSceneUbo = CreateMultiviewUbo(this.getEngine(), "scene_multiview");
        this._multiviewSceneUboIsActive = true;
    };

    Scene.prototype.createSceneUniformBuffer = function (name?: string, trackUBOsInFrameOrOptions?: boolean | ICreateSceneUboOptions): UniformBuffer {
        const forceMono = typeof trackUBOsInFrameOrOptions === "object" && !!trackUBOsInFrameOrOptions?.forceMono;
        if (!forceMono && this._multiviewSceneUboIsActive) {
            const trackUBOsInFrame = typeof trackUBOsInFrameOrOptions === "boolean" ? trackUBOsInFrameOrOptions : trackUBOsInFrameOrOptions?.trackUBOsInFrame;
            return CreateMultiviewUbo(this.getEngine(), name, trackUBOsInFrame);
        }
        // Cast to implementation signature: .call() on overloaded functions resolves to the last overload in TypeScript,
        // but the original implementation correctly handles boolean | ICreateSceneUboOptions | undefined.
        return (CurrentCreateSceneUniformBuffer as (this: Scene, name?: string, trackUBOsInFrameOrOptions?: boolean | ICreateSceneUboOptions) => UniformBuffer).call(
            this,
            name,
            trackUBOsInFrameOrOptions
        );
    };

    Scene.prototype._updateMultiviewUbo = function (viewR?: Matrix, projectionR?: Matrix) {
        if (viewR && projectionR) {
            viewR.multiplyToRef(projectionR, this._transformMatrixR);
        }

        if (!this._inverseProjectionMatrix) {
            this._inverseProjectionMatrix = new Matrix();
        }
        this._projectionMatrix.invertToRef(this._inverseProjectionMatrix);

        if (viewR && projectionR) {
            viewR.multiplyToRef(projectionR, TmpVectors.Matrix[0]);
            Frustum.GetRightPlaneToRef(TmpVectors.Matrix[0], this._frustumPlanes[3]); // Replace right plane by second camera right plane
        }

        if (this._multiviewSceneUbo) {
            this._multiviewSceneUbo.updateMatrix("viewProjection", this.getTransformMatrix());
            this._multiviewSceneUbo.updateMatrix("viewProjectionR", this._transformMatrixR);
            this._multiviewSceneUbo.updateMatrix("view", this._viewMatrix);
            this._multiviewSceneUbo.updateMatrix("projection", this._projectionMatrix);
            this._multiviewSceneUbo.updateMatrix("inverseProjection", this._inverseProjectionMatrix);
        }
    };

    Scene.prototype._renderMultiviewToSingleView = function (camera: Camera) {
        // Multiview is only able to be displayed directly for API's such as webXR
        // This displays a multiview image by rendering to the multiview image and then
        // copying the result into the sub cameras instead of rendering them and proceeding as normal from there

        // Render to a multiview texture
        camera._resizeOrCreateMultiviewTexture(
            camera._rigPostProcess && camera._rigPostProcess && camera._rigPostProcess.width > 0 ? camera._rigPostProcess.width : this.getEngine().getRenderWidth(true),
            camera._rigPostProcess && camera._rigPostProcess && camera._rigPostProcess.height > 0 ? camera._rigPostProcess.height : this.getEngine().getRenderHeight(true)
        );
        if (!this._multiviewSceneUbo) {
            this._createMultiviewUbo();
        }
        camera.outputRenderTarget = camera._multiviewTexture;
        this._renderForCamera(camera);
        camera.outputRenderTarget = null;

        // Consume the multiview texture through a shader for each eye
        for (let index = 0; index < camera._rigCameras.length; index++) {
            const engine = this.getEngine();
            this._activeCamera = camera._rigCameras[index];
            engine.setViewport(this._activeCamera.viewport);
            if (this.postProcessManager) {
                this.postProcessManager._prepareFrame();
                this.postProcessManager._finalizeFrame(this._activeCamera.isIntermediate);
            }
        }
    };
}
